import { Loading } from '@carbon/react';
import { useInfiniteScroll } from 'lib/hooks/useInfiniteScroll';
import { EmptyState } from 'components/empty-state';
import { Events as EventsIcon } from 'components/icons';
import {
  type ActivityFeedItem,
  isVersionActivity,
  isEventActivity,
} from './types';
import { VersionActivity } from './version-activity';
import { EventActivity } from './event-activity';
import { SummaryActivity } from './summary-activity';
import style from './activity-feed-list.module.scss';

interface ActivityFeedListProps {
  items: ActivityFeedItem[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  error?: string;
}

export const ActivityFeedList = ({
  items,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  error,
}: ActivityFeedListProps) => {
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore,
    hasMore,
    loading: loading || loadingMore,
  });

  if (loading && items.length === 0) {
    return (
      <div className={style.loadingContainer}>
        <Loading description="Loading activity..." withOverlay={false} small />
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <div className={style.errorContainer}>
        <p>{error}</p>
      </div>
    );
  }

  if (!loading && items.length === 0) {
    return (
      <div className={style.emptyContainer}>
        <EmptyState
          IconComponent={EventsIcon}
          headerText="No activity yet"
          helperText="Activity will appear here as changes are made."
          className={style.emptyPanel}
        />
      </div>
    );
  }

  const reversedItems = [...items].reverse();

  return (
    <div className={style.listContainer}>
      <ul className={style.list}>
        {reversedItems.map((item) => {
          // Server-summarized rows (interest certification) render as cards;
          // everything else keeps the raw version/event rendering.
          if (item.summary) {
            return (
              <SummaryActivity
                key={item.activityId}
                item={{ ...item, summary: item.summary }}
              />
            );
          }
          if (isVersionActivity(item)) {
            return <VersionActivity key={item.activityId} item={item} />;
          }
          if (isEventActivity(item)) {
            return <EventActivity key={item.activityId} item={item} />;
          }
          return null;
        })}
      </ul>

      <div ref={sentinelRef} className={style.sentinel}>
        {loadingMore && (
          <div className={style.loadingMore}>
            <Loading description="Loading more..." withOverlay={false} small />
          </div>
        )}
      </div>
    </div>
  );
};
